import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ITicket } from "../ticket-module/models/ITicket";
import { getAllDepartments, getAllTickets, getDepartmentUsers } from "../ticket-module/services/ticketService";
import { TICKET_STATUS_IDS, TICKET_STATUS_OPTIONS } from "../ticket-module/ticketStatus";
import TicketCard from "../ticket-module/components/TicketCard";
import { getUserToken } from "../utils/Util";

interface Department { id: string; name: string; }
type Paging = { pageIndex: number; totalCount: number; totalPages: number; hasNextPage: boolean; hasPreviousPage: boolean };

const PAGE_SIZE = 50;

function formatDay(d: string) {
  return new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function TicketSearchPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // Arama kriterleri URL'de tutulur — sayfa yenilense veya geri gelinse de sonuçlar kaybolmaz.
  const statusId     = params.get("statusId") ?? "";
  const departmentId = params.get("departmentId") ?? "";
  const fromDate     = params.get("fromDate") ?? "";
  const toDate       = params.get("toDate") ?? "";
  const searchTerm   = params.get("searchTerm") ?? "";
  const pageIndex    = Number(params.get("page") ?? 0) || 0;
  const hideClosed   = params.get("hideClosed") === "1";

  const [tickets, setTickets] = useState<ITicket[]>([]);
  const [paging, setPaging] = useState<Paging | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [departments, setDepartments] = useState<Department[]>([]);
  const [myDeptIds, setMyDeptIds] = useState<string[]>([]);
  const [localSearch, setLocalSearch] = useState("");
  const [showCriteria, setShowCriteria] = useState(false);

  // Kriter formu (Uygula'ya basılana kadar URL'ye yazılmaz)
  const [fStatus, setFStatus] = useState(statusId);
  const [fDept, setFDept] = useState(departmentId);
  const [fFrom, setFFrom] = useState(fromDate);
  const [fTo, setFTo] = useState(toDate);
  const [fTerm, setFTerm] = useState(searchTerm);

  const currentUserId = getUserToken()?.userId ?? "";
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    getAllDepartments().then((r) => setDepartments(r.data ?? [])).catch(() => {});
    const YAZILIM_DEPT_ID = "a1adf997-e85a-40da-823c-7477e0d2cf09";
    const DESTEK_DEPT_ID  = "c631c065-2de5-4327-acfa-57767f390820";
    Promise.all([getDepartmentUsers(YAZILIM_DEPT_ID), getDepartmentUsers(DESTEK_DEPT_ID)])
      .then(([y, d]) => {
        const ids: string[] = [];
        if ((y.data ?? []).includes(currentUserId)) ids.push(YAZILIM_DEPT_ID);
        if ((d.data ?? []).includes(currentUserId)) ids.push(DESTEK_DEPT_ID);
        setMyDeptIds(ids);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFStatus(statusId); setFDept(departmentId); setFFrom(fromDate); setFTo(toDate); setFTerm(searchTerm);
    setLoading(true);
    setError("");
    const base = {
      departmentId: departmentId || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      searchTerm: searchTerm || undefined,
      pageIndex,
      pageSize: PAGE_SIZE,
    };
    // Backend, durum verilmediğinde kapalı ticketleri hariç tutuyor. "Tüm Durumlar"da
    // kapalıları da getirmek için onları ayrı bir istekle çekip sonuçlara ekliyoruz.
    const requests = statusId
      ? [getAllTickets({ ...base, statusId })]
      : [getAllTickets(base), getAllTickets({ ...base, statusId: TICKET_STATUS_IDS.kapali })];
    Promise.all(requests)
      .then((responses) => {
        const seen = new Set<string>();
        const items: ITicket[] = [];
        responses.forEach((r) => (r.data?.items ?? []).forEach((t: ITicket) => {
          if (!seen.has(t.id)) { seen.add(t.id); items.push(t); }
        }));
        const pagings = responses.map((r) => r.data?.paging).filter(Boolean) as Paging[];
        setTickets(items);
        setPaging(pagings.length === 0 ? null : {
          pageIndex,
          totalCount: pagings.reduce((s, p) => s + p.totalCount, 0),
          totalPages: Math.max(...pagings.map((p) => p.totalPages)),
          hasNextPage: pagings.some((p) => p.hasNextPage),
          hasPreviousPage: pageIndex > 0,
        });
      })
      .catch(() => { setTickets([]); setPaging(null); setError("Ticketlar yüklenemedi."); })
      .finally(() => setLoading(false));
  }, [statusId, departmentId, fromDate, toDate, searchTerm, pageIndex]);

  const updateParams = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setParams(next, { replace: false });
  };

  const applyCriteria = () => {
    updateParams({
      statusId: fStatus || undefined,
      departmentId: fDept || undefined,
      fromDate: fFrom || undefined,
      toDate: fTo || undefined,
      searchTerm: fTerm.trim() || undefined,
      page: undefined,
    });
    setShowCriteria(false);
  };

  const goPage = (p: number) => {
    updateParams({ page: p > 0 ? String(p) : undefined });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closedCount = tickets.filter((t) => t.ticketStatusId === TICKET_STATUS_IDS.kapali).length;
  const q = localSearch.trim().toLowerCase();
  const visible = tickets
    .filter((t) => !hideClosed || t.ticketStatusId !== TICKET_STATUS_IDS.kapali)
    .filter((t) => !q ||
      (t.title ?? "").toLowerCase().includes(q) ||
      (t.mainCustomerName ?? "").toLowerCase().includes(q) ||
      (t.ticketDepartmentName ?? "").toLowerCase().includes(q) ||
      (t.ticketNo ?? "").toLowerCase().includes(q));

  const chips: { key: string; label: string; clear: Record<string, undefined> }[] = [];
  if (searchTerm)   chips.push({ key: "t", label: `"${searchTerm}"`, clear: { searchTerm: undefined } });
  if (statusId)     chips.push({ key: "s", label: TICKET_STATUS_OPTIONS.find((o) => o.id === statusId)?.label ?? "Durum", clear: { statusId: undefined } });
  if (departmentId) chips.push({ key: "d", label: departments.find((d) => d.id === departmentId)?.name ?? "Departman", clear: { departmentId: undefined } });
  if (fromDate)     chips.push({ key: "f", label: `${formatDay(fromDate)}'den`, clear: { fromDate: undefined } });
  if (toDate)       chips.push({ key: "to", label: `${formatDay(toDate)}'e kadar`, clear: { toDate: undefined } });

  const inp: React.CSSProperties = {
    width: "100%", padding: "9px 12px", borderRadius: 10, border: "1.5px solid #e5e7eb",
    fontSize: "0.85rem", outline: "none", boxSizing: "border-box", background: "#fff",
  };
  const lbl: React.CSSProperties = { fontSize: "0.75rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 };

  return (
    <div className="destek-page" style={{ padding: isMobile ? "16px 12px" : "24px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={() => navigate("/destek")} title="Destek sayfasına dön"
          style={{ width: 38, height: 38, borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <i className="bi bi-arrow-left"></i>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 style={{ fontWeight: 800, color: "#111827", margin: 0 }}>
            <i className="bi bi-search me-2 text-primary"></i>Ticket Arama Sonuçları
          </h4>
          <p style={{ color: "#6b7280", fontSize: "0.85rem", margin: "4px 0 0" }}>
            {loading ? "Aranıyor…" : `${paging?.totalCount ?? tickets.length} ticket bulundu`}
            {!loading && closedCount > 0 && ` · bu sayfada ${closedCount} kapalı`}
          </p>
        </div>
        <button onClick={() => setShowCriteria((s) => !s)}
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 12, border: "1.5px solid #6366f1", background: showCriteria ? "#eef2ff" : "#fff", color: "#4f46e5", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
          <i className="bi bi-sliders"></i>{!isMobile && "Kriterleri Değiştir"}
        </button>
      </div>

      {/* Aktif kriterler */}
      {chips.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {chips.map((c) => (
            <span key={c.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe", borderRadius: 20, padding: "4px 6px 4px 12px", fontSize: "0.78rem", fontWeight: 600 }}>
              {c.label}
              <button onClick={() => updateParams({ ...c.clear, page: undefined })} title="Kaldır"
                style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", padding: 0, display: "flex" }}>
                <i className="bi bi-x-circle-fill"></i>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Kriter formu */}
      {showCriteria && (
        <div style={{ background: "#fff", border: "1.5px solid #e0e7ff", borderRadius: 14, padding: 16, marginBottom: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Arama Terimi</label>
              <input value={fTerm} onChange={(e) => setFTerm(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") applyCriteria(); }} placeholder="Ticket başlığı veya ticket no..." style={inp} />
            </div>
            <div>
              <label style={lbl}>Durum</label>
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
                <option value="">Tüm Durumlar</option>
                {TICKET_STATUS_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Departman</label>
              <select value={fDept} onChange={(e) => setFDept(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
                <option value="">Tüm Departmanlar</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Başlangıç Tarihi</label>
              <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} style={inp} />
            </div>
            <div>
              <label style={lbl}>Bitiş Tarihi</label>
              <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} style={inp} />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <button onClick={() => { setFStatus(""); setFDept(""); setFFrom(""); setFTo(""); setFTerm(""); }}
                style={{ flex: 1, padding: "9px 0", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                Temizle
              </button>
              <button onClick={applyCriteria}
                style={{ flex: 1, padding: "9px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
                <i className="bi bi-search me-1"></i>Ara
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sonuçlar içinde arama + kapalı filtre */}
      <div className="destek-filter-box">
        <div className="destek-search-bar" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: "1 1 240px" }}>
            <i className="bi bi-search" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "0.9rem" }}></i>
            <input type="text" placeholder="Sonuçlar içinde ara: başlık, müşteri, departman, ticket no..."
              value={localSearch} onChange={(e) => setLocalSearch(e.target.value)} className="destek-search-input" />
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: "0.83rem", fontWeight: 600, color: "#374151", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={hideClosed} onChange={(e) => updateParams({ hideClosed: e.target.checked ? "1" : undefined })}
              style={{ width: 16, height: 16, cursor: "pointer" }} />
            <i className="bi bi-lock-fill" style={{ color: "#9ca3af" }}></i>Kapalı ticketleri gizle
          </label>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="destek-state-center"><div className="spinner-border text-primary" role="status" /></div>
      ) : error ? (
        <div className="destek-state-center" style={{ color: "#dc2626" }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "2rem" }}></i>
          <p style={{ marginTop: 10 }}>{error}</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="destek-state-center">
          <i className="bi bi-inbox" style={{ fontSize: "2.5rem", color: "#d1d5db" }}></i>
          <p style={{ color: "#9ca3af", marginTop: "10px", fontSize: "0.9rem" }}>
            {tickets.length > 0 ? "Filtrelere uyan ticket yok." : "Arama sonucu bulunamadı."}
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(420px, 1fr))", gap: 12 }}>
          {visible.map((t) => (
            <TicketCard key={t.id} ticket={t} currentUserId={currentUserId} currentUserDeptIds={myDeptIds} />
          ))}
        </div>
      )}

      {/* Sayfalama */}
      {!loading && paging && paging.totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 20 }}>
          <button onClick={() => goPage(pageIndex - 1)} disabled={!paging.hasPreviousPage}
            style={{ padding: "8px 14px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: paging.hasPreviousPage ? "#374151" : "#d1d5db", cursor: paging.hasPreviousPage ? "pointer" : "not-allowed", fontWeight: 600 }}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <span style={{ fontSize: "0.85rem", color: "#374151", fontWeight: 600 }}>
            {pageIndex + 1} / {paging.totalPages}
          </span>
          <button onClick={() => goPage(pageIndex + 1)} disabled={!paging.hasNextPage}
            style={{ padding: "8px 14px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: paging.hasNextPage ? "#374151" : "#d1d5db", cursor: paging.hasNextPage ? "pointer" : "not-allowed", fontWeight: 600 }}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
      )}
    </div>
  );
}
